/**
 * The client Overview's cards, fed by `GET /api/dashboard/client/:fn`, as one
 * hairline grid. Navigation goes through the profile's tab URL API so the
 * cards land on the right tab (and focus) within the same page.
 */
import type { FC, ReactNode } from "react";

import { CardGrid } from "@/components/surface";
import { agentPanelStore } from "@/features/agents/agentPanelStore";

import type { ClientProfileUrlApi } from "../../clientProfile/hooks/useClientProfileUrlState";
import type { ClientDashboard } from "../types";
import type { DashboardCardsState } from "./DashboardShell";
import { AttentionCard } from "./cards/AttentionCard";
import { CasesCard } from "./cards/CasesCard";
import { IntakesToReviewCard } from "./cards/IntakesToReviewCard";
import { KeyDatesCard } from "./cards/KeyDatesCard";
import { TodayCard } from "./cards/TodayCard";

type Props = DashboardCardsState<ClientDashboard> & {
  clientId: string;
  url: Pick<ClientProfileUrlApi, "setTab">;
  /** Sits beside Key dates ("Ready to file"); Key dates spans the row without it. */
  readiness?: ReactNode;
};

export const ClientCards: FC<Props> = ({
  summary,
  isLoading,
  error,
  refresh,
  clientId,
  url,
  readiness
}) => {
  const common = { isLoading, error, onRetry: refresh };
  const inFlight = summary?.intakes_in_flight ?? [];
  return (
    <CardGrid cols={2} data-testid="client-cards">
      {summary?.attention_threads.length || isLoading ? (
        <AttentionCard
          items={summary?.attention_threads}
          onViewAll={() => agentPanelStore.openWith({ agent: "client" })}
          span="full"
          {...common}
        />
      ) : null}
      <TodayCard
        mode="client"
        today={summary?.today}
        upcoming={summary?.upcoming}
        onViewAll={() => url.setTab("reminders")}
        onChanged={refresh}
        span="full"
        {...common}
      />
      <KeyDatesCard
        items={summary?.key_dates}
        onViewAll={() => url.setTab("immigration_overview")}
        span={readiness ? undefined : "full"}
        {...common}
      />
      {readiness}
      <IntakesToReviewCard
        mode="client"
        awaitingReview={summary?.intakes_awaiting_review}
        inFlight={inFlight}
        onViewAll={() => url.setTab("outgoing_intakes")}
        span="full"
        {...common}
      />
      <CasesCard
        mode="client"
        cases={summary?.cases}
        onViewAll={() => url.setTab("cases")}
        span="full"
        {...common}
      />
    </CardGrid>
  );
};
